/**
 * Operations → Surface — the home of the Operations Attention System
 * (ADR-0119). Three tabs:
 *
 *   - **By status** (default) — the belt: the rule-surfaced attention rows in
 *     their blocks, for awareness. A row leaves when its rule stops matching
 *     (#969 removed the row actions).
 *   - **By firm** — the same belt rows re-pivoted onto the firm that owns the
 *     row (`groupByFirm`, client-side).
 *   - **Pinned** — her own PRIVATE watches: matters she flagged to keep an eye
 *     on for a reason no rule captures. A status-shaped card (#541 Slice 2:
 *     current book-card detail + notes thread + "+ To-do") with Un-pin;
 *     nothing auto-recedes (un-pin is by hand or MCP only).
 *
 * To-dos live on the Dashboard. The book-card "Pin" button
 * (surface-trigger.tsx) and the case-detail panel's pin toggle (#541) create a
 * pin (a private watch); un-pin reuses the task `clear` action.
 */

import { useState } from "react";
import { todayInPT, type BeltBlock, type DashboardRow } from "@command-center/shared";
import { BookHeading } from "@/components/book-heading";
import { BeltRowCard } from "@/components/belt-row-card";
import { CaseDetailPanel } from "@/components/case-detail-panel";
import { PinnedCard } from "@/components/pinned-card";
import { SegmentedTab } from "@/components/ui/segmented-tab";
import { useMe } from "@/lib/use-me";
import { useSurface } from "@/lib/use-surface";
import { type CaseDetail } from "@/lib/case-detail";

type SurfaceTab = "belt" | "pinned" | "by-firm";

/** The Surface, re-pivoted onto the firm that owns each row (the
 *  by-firm subtab, 2026-06-23). The belt's block = criterion → here the block =
 *  firm; the criterion (the block's label) moves onto the row. Same rows, axes
 *  swapped. Rows whose `firmId` is null (LegalFlare's own rows) collect under
 *  the "LegalFlare" bucket. A single record that owes two parties
 *  (e.g. an OF receipt and an LF receipt) is two rows and lands in both buckets —
 *  the unit is the row, not the record. */
type FirmGroup = {
  /** `firmId`, or the sentinel for the LegalFlare bucket. */
  key: string;
  /** Section header: the firm's name, or "LegalFlare". */
  name: string;
  isLegalFlare: boolean;
  /** Each row carried with the criterion it surfaced under (the source block's
   *  label — reused as-is for now; short firm-phrased copy is a later pass). */
  rows: { row: DashboardRow; criterion: string }[];
};

const LEGALFLARE_BUCKET = "__legalflare__";

/** Regroup the belt (by-criterion blocks) into by-firm buckets. Worst-first
 *  within a firm (oldest `dueBy` on top, like the belt); firms ordered biggest-
 *  offender-first by outstanding count, with the LegalFlare bucket pinned last.
 *  Exported for the by-firm grouping test. */
export function groupByFirm(belt: BeltBlock[]): FirmGroup[] {
  const groups = new Map<string, FirmGroup>();
  for (const block of belt) {
    for (const row of block.rows) {
      const key = row.firmId ?? LEGALFLARE_BUCKET;
      let group = groups.get(key);
      if (!group) {
        group = {
          key,
          name: row.firmId ? row.firmName ?? "Unknown firm" : "LegalFlare",
          isLegalFlare: key === LEGALFLARE_BUCKET,
          rows: [],
        };
        groups.set(key, group);
      }
      group.rows.push({ row, criterion: block.label });
    }
  }
  const ordered = [...groups.values()];
  for (const group of ordered) {
    group.rows.sort((a, b) =>
      (a.row.dueBy ?? "").localeCompare(b.row.dueBy ?? ""),
    );
  }
  ordered.sort((a, b) => {
    if (a.isLegalFlare !== b.isLegalFlare) return a.isLegalFlare ? 1 : -1;
    return b.rows.length - a.rows.length;
  });
  return ordered;
}

export function SurfacePage() {
  const surface = useSurface();
  const me = useMe();
  const [tab, setTab] = useState<SurfaceTab>("belt");
  const [detail, setDetail] = useState<CaseDetail | null>(null);
  const today = todayInPT();

  if (surface.isLoading) {
    return <div className="p-8 text-sm text-plane-muted">Loading…</div>;
  }
  if (surface.isError) {
    return (
      <div className="p-8 text-sm text-error" data-testid="surface-error">
        Couldn't load the Surface. It will retry automatically.
      </div>
    );
  }

  const belt = surface.data?.belt ?? [];
  const pinned = surface.data?.pinned ?? [];
  // The tab badge counts ROWS across every block — "how many things need me",
  // not how many categories. Blocks themselves are the in-page sections.
  const beltRowCount = belt.reduce((sum, block) => sum + block.rows.length, 0);
  // The by-firm re-pivot of the same belt rows (the by-firm subtab). Both belt
  // subtabs are one belt viewed two ways, so the tab badge shows the SAME total
  // (`beltRowCount`) on both — "how many things need me", regardless of view —
  // rather than switching units to a firm count beside an identical-looking chip.
  const byFirmGroups = groupByFirm(belt);

  return (
    <section className="mx-auto max-w-4xl p-6" data-testid="surface">
      <BookHeading phaseKey="surface">
        <SurfaceTabs
          tab={tab}
          onSelect={setTab}
          beltCount={beltRowCount}
          byFirmCount={beltRowCount}
          pinnedCount={pinned.length}
        />
      </BookHeading>

      {tab === "belt" ? (
        <div className="flex flex-col gap-6" data-testid="surface-belt">
          {beltRowCount === 0 ? (
            <p
              className="text-sm text-plane-muted"
              data-testid="surface-belt-empty"
            >
              All done — everything's handled.
            </p>
          ) : (
            // Stacked, labeled sections in block order. The top non-empty block
            // is the active one; clearing its rows makes the section vanish and
            // the next becomes top — "work one block → it completes → next opens"
            // (no accordion state machine in v1, deliberately).
            belt.map((block) => (
              <section
                key={block.id}
                data-testid={`belt-block-${block.id}`}
                className="flex flex-col gap-2"
              >
                <h3
                  className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-plane-muted"
                  data-testid={`belt-block-label-${block.id}`}
                >
                  <span>{block.label}</span>
                  <span
                    className="rounded-full bg-plane-muted/15 px-1.5 text-[10px] font-semibold leading-none text-plane-muted"
                    data-testid={`belt-block-count-${block.id}`}
                  >
                    {block.rows.length}
                  </span>
                </h3>
                {block.rows.map((row) => (
                  <BeltRowCard
                    key={row.id}
                    row={row}
                    today={today}
                    pinned={pinned}
                  />
                ))}
              </section>
            ))
          )}
        </div>
      ) : tab === "by-firm" ? (
        <div className="flex flex-col gap-6" data-testid="surface-by-firm">
          {byFirmGroups.length === 0 ? (
            <p
              className="text-sm text-plane-muted"
              data-testid="surface-by-firm-empty"
            >
              All done — everything's handled.
            </p>
          ) : (
            // Same belt rows, grouped under the firm that owns each row. The
            // section header is the firm (or LegalFlare); the criterion the row
            // surfaced under moves onto the card's record line.
            byFirmGroups.map((group) => (
              <section
                key={group.key}
                data-testid={`firm-block-${group.key}`}
                className="flex flex-col gap-2"
              >
                <h3
                  className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-plane-muted"
                  data-testid={`firm-block-label-${group.key}`}
                >
                  <span>{group.name}</span>
                  <span
                    className="rounded-full bg-plane-muted/15 px-1.5 text-[10px] font-semibold leading-none text-plane-muted"
                    data-testid={`firm-block-count-${group.key}`}
                  >
                    {group.rows.length}
                  </span>
                </h3>
                {group.rows.map(({ row, criterion }) => (
                  <BeltRowCard
                    key={row.id}
                    row={row}
                    today={today}
                    pinned={pinned}
                    recordLine={criterion}
                  />
                ))}
              </section>
            ))
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2" data-testid="surface-pinned">
          {pinned.length === 0 ? (
            <p
              className="text-sm text-plane-muted"
              data-testid="surface-pinned-empty"
            >
              Nothing pinned. Hit “Pin” on any book's card — or the pin in a
              case's detail panel — to keep an eye on it.
            </p>
          ) : (
            pinned.map((pin) => (
              <PinnedCard
                key={pin.task.id}
                pin={pin}
                openDetail={setDetail}
                viewerEmail={me.data?.email ?? ""}
              />
            ))
          )}
        </div>
      )}

      {detail ? (
        <CaseDetailPanel
          key={
            detail.actionTarget?.placementId ??
            detail.leadActionTarget?.leadId ??
            detail.name
          }
          detail={detail}
          onClose={() => setDetail(null)}
          testId="surface-detail-panel"
        />
      ) : null}
    </section>
  );
}

/** The Belt | Pinned segmented toggle — an in-page tablist (not a route), so the
 *  just-landed sidebar nav is untouched. Mirrors the Dashboard's viewer toggle. */
function SurfaceTabs({
  tab,
  onSelect,
  beltCount,
  byFirmCount,
  pinnedCount,
}: {
  tab: SurfaceTab;
  onSelect: (tab: SurfaceTab) => void;
  beltCount: number;
  byFirmCount: number;
  pinnedCount: number;
}) {
  return (
    <div
      role="tablist"
      aria-label="Surface"
      data-testid="surface-tabs"
      className="inline-flex overflow-hidden rounded-md border border-border bg-muted/30"
    >
      <SegmentedTab
        active={tab === "belt"}
        onClick={() => onSelect("belt")}
        label="By status"
        count={beltCount}
        testId="surface-tab-belt"
      />
      <SegmentedTab
        active={tab === "by-firm"}
        onClick={() => onSelect("by-firm")}
        label="By firm"
        count={byFirmCount}
        testId="surface-tab-by-firm"
      />
      <SegmentedTab
        active={tab === "pinned"}
        onClick={() => onSelect("pinned")}
        label="Pinned"
        count={pinnedCount}
        testId="surface-tab-pinned"
      />
    </div>
  );
}

// PinnedCard (the status-shaped pinned row, #541 Slice 2) and the
// cardToDetail projection live in components/pinned-card.tsx.
